import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';

import type { PopupData, Service } from 'webapp-types';
import { BillingCycle, PlanType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { UserSettingsKey } from 'webapp/enums/user-settings';

import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

interface Feature {
	icon: IconName;
	iconStyle: ReturnType<typeof htmlSafe>;
	info: string;
}

type FeatureSet = Feature[];

enum FeatureColor {
	Teal = '2EC1D6',
	Blue = '1D9AD6',
	Gray = '8E9299',
	Orange = 'F47920',
	Green = '01B45E',
}

// popup is used for expired higher plan trial (paid)

export default class BillingPopupHigherPlanTrialExpiredComponent extends PopupInnerModalComponent<PopupData.Billing.HigherPlanTrialExpired> {
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('user-settings') declare userSettingsService: Service.UserSettings;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.planType =
			this.billingService.getPlanFromActiveOrExpiredPromotion()?.planType ||
			this.billingService.activePlan?.planType;
		this.trackingOrigin = 'paid_trial';

		this.analyticsService.event(TrackingEvent.TRIAL_EXPIRED_POPUP_SHOWN, {
			origin,
		});

		next(this, () => {
			void this.userSettingsService.save(UserSettingsKey.TRIAL_EXPIRED_POPUP_SEEN, true);
		});
	}

	get features() {
		if (this.isProfessionalPlan) {
			return this.professionalFeatures;
		} else if (this.isGrowthPlan) {
			return this.growthFeatures;
		}

		return this.generalFeatures;
	}

	get name() {
		return this.currentUserService.model!.firstName;
	}

	get isProfessionalPlan() {
		return this.planType === PlanType.PROFESSIONAL;
	}

	get isGrowthPlan() {
		return this.planType === PlanType.GROWTH;
	}

	get isOtherPlan() {
		return !this.isGrowthPlan && !this.isProfessionalPlan;
	}

	get planName() {
		return this.targetPlan?.name;
	}

	get title() {
		const activePlanName = this.planName;
		const activePlanNameText = activePlanName
			? ` on the <strong>${activePlanName} plan</strong>`
			: '';

		return `your free Databox trial${activePlanNameText} has just ended`;
	}

	get description() {
		const otherText = 'Other metrics from your data sources will continue syncing.',
			willRemainText = 'will remain,',
			editedText = 'but can’t be edited',
			accountText = 'until your account is updated';

		if (this.isProfessionalPlan) {
			return `Calculated and built metrics created from spreadsheets ${willRemainText} ${editedText} ${accountText}. ${otherText}`;
		} else if (this.isGrowthPlan) {
			return `Metrics created from datasets ${willRemainText} ${editedText} and no additional data will be synced ${accountText}. ${otherText}`;
		}

		return `Data refresh has been put on hold ${accountText}. Existing dashboards, reports, alerts, and scorecards ${willRemainText} but there may be gaps in your data if no action is taken.`;
	}

	get accountOwner() {
		return this.accountService.accountOwner;
	}

	get canBookACall(): boolean {
		return !this.currentUserService.isClientUserInPaidClientSpace;
	}

	get targetPlan() {
		const currentBillingCycle =
			this.billingService.activePlan?.billingCycle || BillingCycle.MONTHLY;
		return this.billingService.availablePlans.find(
			(plan) => plan.planType === this.planType && plan.billingCycle === currentBillingCycle,
		);
	}

	get upgradePlanButtonText() {
		const planName = this.planName,
			planNameText = planName ? ` to ${planName}` : '';

		return `Upgrade${planNameText}`;
	}

	get canRequestSetupHelp(): boolean {
		return !this.currentUserService.isClientUserInPaidClientSpace;
	}

	getIconStyle(hexColorCode: string) {
		return htmlSafe(`background-color: #${hexColorCode};`);
	}

	@action
	upgradePlan() {
		const targetPlan = this.targetPlan;

		if (targetPlan) {
			this.billingService.switchPlan(targetPlan, {
				billingPeriodChangePopup: 'paid_trial_expired_popup',
			});
		}
	}

	@action
	requestSetupHelp() {
		this.accountService.requestSetupHelp();
		this.analyticsService.event(TrackingEvent.CALL_INITIATED, { origin: this.trackingOrigin });
	}

	private planType: PlanType | undefined;
	private trackingOrigin: string | undefined;

	private readonly spreadsheetFeature = this.createFeature(
		'Table',
		FeatureColor.Green,
		'Integrate data from <b>spreadsheets</b>',
	);

	private readonly aiSummaryFeature = this.createFeature(
		'Ai',
		FeatureColor.Orange,
		'Get <b>AI performance summaries</b>',
	);

	private readonly generalFeatures: FeatureSet = [
		this.createFeature(
			'CloudUpload',
			FeatureColor.Teal,
			'Connect <b>unlimited</b> data sources',
		),
		this.spreadsheetFeature,
		this.createFeature(
			'Databoards',
			FeatureColor.Blue,
			'Build <b>unlimited</b> dashboards and reports',
		),
		this.createFeature(
			'Calculator',
			FeatureColor.Gray,
			'Build <b>custom</b> and <b>calculated metrics</b>',
		),
		this.aiSummaryFeature,
	];

	private readonly professionalFeatures: FeatureSet = [
		this.createFeature('Refresh', FeatureColor.Teal, 'Have your <b>data synced hourly</b>'),
		this.spreadsheetFeature,
		this.createFeature(
			'Calendar',
			FeatureColor.Blue,
			'Visualize data with <b>custom date ranges</b>',
		),
		this.createFeature('Calculator', FeatureColor.Gray, 'Create <b>calculated metrics</b>'),
		this.createFeature('Filter', FeatureColor.Orange, 'Build <b>metrics</b>'),
		this.createFeature('Team', FeatureColor.Teal, 'Collaborate with <b>unlimited users</b>'),
	];

	private readonly growthFeatures: FeatureSet = [
		this.createFeature(
			'Merge',
			FeatureColor.Teal,
			'<b>Build and merge datasets</b> from 2+ sources',
		),
		this.createFeature('DrillDown', FeatureColor.Green, 'Drill down to <b>raw-level data</b>'),
		this.createFeature(
			'Databoards',
			FeatureColor.Blue,
			'Add <b>dynamic text elements</b> to reports',
		),
		this.createFeature('CloudUpload', FeatureColor.Gray, 'Connect <b>SQL data sources</b>'),
		this.aiSummaryFeature,
	];

	private createFeature(icon: IconName, color: FeatureColor, info: string): Feature {
		return {
			icon,
			iconStyle: this.getIconStyle(color),
			info,
		};
	}
	<template>
		<div class="title title--vertical">
			<CommonUiIconComponent @name="Rocket" @size="lg" />
			<span class="text">{{this.name}}, {{{this.title}}}.</span>
		</div>
		<div class="content">
			{{#if this.billingService.isAgencyAdmin}}
				<div class="description">{{this.description}}</div>
				<div class="feature-list">
					<div class="feature-list__header">
						By upgrading, you will get back the access to:
					</div>
					<div class="feature-list__content">
						{{#each this.features as |feature|}}
							<div class="feature">
								<div class="feature__icon" style={{feature.iconStyle}}>
									<CommonUiIconComponent @name={{feature.icon}} @size="sm" />
								</div>
								<div class="feature__info">
									{{{feature.info}}}
								</div>
							</div>
						{{/each}}
					</div>
				</div>

				<div class="controls2 controls2--center">
					<SubmitButtonComponent
						@text={{this.upgradePlanButtonText}}
						@size="big"
						@icon="ArrowUpCircleFill"
						@onTrigger={{this.upgradePlan}}
					/>
					{{#if this.canRequestSetupHelp}}
						<SubmitButtonComponent
							@text="Book a call"
							@size="big"
							@theme="green-outlined"
							@onTrigger={{this.requestSetupHelp}}
						/>
					{{/if}}

					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@onTrigger={{this.close}}
					/>
				</div>
			{{else}}
				<p>
					In order to fully access your account,
					<strong>please contact your account owner or any administrator</strong>
					to decide how to continue.
				</p>
				<div class="ownerInfo">
					<AvatarOrInitialsComponent
						@name={{this.accountOwner.name}}
						@avatar={{this.accountOwner.avatar}}
					/>
					<div class="ownerName">
						<div class="name">
							<strong>{{this.accountOwner.name}}</strong>
							(Account owner)
						</div>
						<a class="email" href="mailto:{{this.accountOwner.email}}">
							{{this.accountOwner.email}}
						</a>
					</div>
				</div>
			{{/if}}
		</div>
	</template>
}
